import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Button from "@mui/material/Button";
import Switch from "@mui/material/Switch";
import Grow from "@mui/material/Grow";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import "../App.css";

/*
    DSGVO/GDPR cookie & tracking consent banner - the webapp counterpart of
    the homepage's CookieConsent.astro, with the same wording and the same two
    panels, so a visitor coming from underwaterly.com sees the same thing.

    The UI is rebuilt in React rather than reusing the homepage's markup +
    imperative DOM wiring, because that script toggles `hidden` attributes and
    checkbox state directly and would fight React over the same nodes. Only the
    storage half is shared, via window.underwaterlyConsent - see
    public/js/cookie-consent.js and CookieConsentProvider.

    Using MUI's Dialog also gets us what the homepage banner is still missing:
    focus moves into the dialog, Tab is trapped inside it, and the content
    behind it is inert for screen readers.
*/

const PRIVACY_POLICY_URL = "https://www.underwaterly.com/privacy-policy/";

export default function CookieConsentDialog({
  open,
  panel,
  setPanel,
  consent,
  onDecide,
  onClose,
}) {
  const [analyticsChecked, setAnalyticsChecked] = useState(false);

  // Reflect the stored decision whenever the dialog opens, so reopening it
  // from the cookie settings shows what is actually in effect rather than a
  // leftover toggle position from last time.
  useEffect(() => {
    if (open) setAnalyticsChecked(Boolean(consent?.analytics));
  }, [open, consent]);

  // Before a first decision there is nothing to fall back to, so the dialog
  // cannot be dismissed - every way out is one of the choices below. Once a
  // decision exists, Escape and the backdrop just leave it unchanged.
  const dismissible = Boolean(consent);

  return (
    <Dialog
      open={open}
      onClose={dismissible ? onClose : undefined}
      disableEscapeKeyDown={!dismissible}
      TransitionComponent={Grow}
      aria-labelledby="cookie--consent--heading"
      maxWidth="xs"
      fullWidth
    >
      <DialogContent className="cookie--consent">
        {panel === "summary" ? (
          <>
            <h2 className="cookie--consent--heading" id="cookie--consent--heading">
              We value your privacy
            </h2>
            <p className="cookie--consent--text">
              We use cookies that are necessary for Underwaterly to work. With
              your consent, we'd also like to use analytics cookies to
              understand how the site is used. You can accept all, reject
              non-essential cookies, or choose in detail. See our{" "}
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>{" "}
              for more.
            </p>
            <div className="cookie--consent--actions">
              <Button
                variant="contained"
                className="button button--primary"
                onClick={() => onDecide(true)}
                fullWidth
              >
                Accept all
              </Button>
              <Button
                variant="contained"
                className="button button--primary"
                onClick={() => onDecide(false)}
                fullWidth
              >
                Reject non-essential
              </Button>
            </div>
            <button
              type="button"
              className="cookie--consent--link--button"
              onClick={() => setPanel("prefs")}
            >
              Manage preferences
            </button>
          </>
        ) : (
          <>
            <h2 className="cookie--consent--heading" id="cookie--consent--heading">
              Cookie preferences
            </h2>

            <div className="cookie--consent--category">
              <div>
                <h3 className="cookie--consent--category--name">Necessary</h3>
                <p className="cookie--consent--category--text">
                  Required for the site to work. Always active.
                </p>
              </div>
              <Switch checked disabled inputProps={{ "aria-label": "Necessary cookies, always active" }} />
            </div>

            <div className="cookie--consent--category">
              <div>
                <h3 className="cookie--consent--category--name">Analytics</h3>
                <p className="cookie--consent--category--text">
                  Helps us understand how visitors use Underwaterly (e.g. Google
                  Analytics). Only loaded with your consent.
                </p>
              </div>
              <Switch
                checked={analyticsChecked}
                onChange={(event) => setAnalyticsChecked(event.target.checked)}
                inputProps={{ "aria-label": "Analytics cookies" }}
              />
            </div>

            <div className="cookie--consent--actions">
              <Button
                variant="contained"
                className="button button--primary"
                onClick={() => onDecide(analyticsChecked)}
                fullWidth
              >
                Save preferences
              </Button>
              <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                className="button button--outlined--grey"
                onClick={() => setPanel("summary")}
                fullWidth
              >
                Back
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
